import { useCallback, useEffect, useRef } from 'react';

/** Native dialog supplies focus containment, Escape, and an inert background. */
export function useModalDialog(
  open: boolean,
  restoreFocusRef?: React.RefObject<HTMLElement>
): React.RefObject<HTMLDialogElement> {
  const ref = useRef<HTMLDialogElement>(null);
  // The expansion trigger is remounted when the modal closes; resolve it at cleanup time.
  const restoreTarget = useCallback(() => restoreFocusRef?.current, [restoreFocusRef]);
  useEffect(() => {
    const dialog = ref.current;
    if (!open || dialog === null) return;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    dialog.showModal();
    document.body.style.overflow = 'hidden';
    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
      const target = restoreTarget() ?? previousFocus;
      if (target?.isConnected) target.focus();
    };
  }, [open, restoreTarget]);
  return ref;
}
